import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, Copy, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { toast as sonner } from 'sonner';
import { API_ERROR_EVENT } from '../../lib/apiErrorModal';

/**
 * Global Action-API Error Modal
 * ---------------------------------------------------------------
 * Mounted ONCE at the App root. Listens for `chanakya:api-error`
 * CustomEvent (see /src/lib/apiErrorModal.js) and pops a themed
 * dialog with the exact server message plus a "Copy Error" button.
 *
 * Design goals:
 *  - Non-blocking (does not swallow the underlying Promise chain,
 *    so page-level onError still runs — the toast is suppressed by
 *    a companion helper in apiErrorModal.js).
 *  - Copy-friendly: single-click clipboard write for support tickets.
 *  - Keyboard-first: Esc closes; focused Close button by default.
 */
export default function ApiErrorModal() {
  const [state, setState] = useState({ open: false, message: '', title: '', hint: '' });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      const { message = '', title = 'Action Failed', hint = '' } = e.detail || {};
      setCopied(false);
      setState({ open: true, message, title, hint });
    };
    window.addEventListener(API_ERROR_EVENT, handler);
    return () => window.removeEventListener(API_ERROR_EVENT, handler);
  }, []);

  const handleClose = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
  }, []);

  const handleCopy = useCallback(async () => {
    const text = state.message || '';
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for insecure contexts / older browsers
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      sonner.success('Error message copied', { duration: 1500 });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      sonner.error('Could not copy — please copy manually');
    }
  }, [state.message]);

  return (
    <Dialog open={state.open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent
        className="max-w-lg"
        data-testid="api-error-modal"
        onOpenAutoFocus={(e) => {
          // Focus the primary action instead of the Close (X) button
          e.preventDefault();
          const el = document.querySelector('[data-testid="api-error-modal-close"]');
          el?.focus?.();
        }}
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-950/60 dark:text-red-400">
              <AlertTriangle className="h-5 w-5" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <DialogTitle
                className="text-base font-semibold"
                data-testid="api-error-modal-title"
              >
                {state.title}
              </DialogTitle>
              <DialogDescription
                className="mt-1 text-xs text-muted-foreground"
                data-testid="api-error-modal-hint"
              >
                {state.hint
                  ? state.hint
                  : 'The server rejected the last action. Details below — copy the exact text if you need to share it with support.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div
          className="mt-1 max-h-[45vh] overflow-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-[13px] leading-relaxed text-foreground"
          data-testid="api-error-modal-message"
        >
          {state.message || 'Unknown error.'}
        </div>

        <DialogFooter className="mt-4 gap-2 sm:justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={handleCopy}
            className="gap-2"
            data-testid="api-error-modal-copy"
          >
            {copied ? (
              <>
                <Check className="h-4 w-4" /> Copied
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" /> Copy Error
              </>
            )}
          </Button>
          <Button
            type="button"
            onClick={handleClose}
            data-testid="api-error-modal-close"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
